import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { User, X } from 'lucide-react';
import Modal from 'components/Modal/Modal';
import CropWorkspace from 'components/ImageCrop/CropWorkspace';
import {
  attachCropDialogChrome,
  PREVIEW_THROTTLE_MS,
  readFileAsDataUrl,
} from 'components/ImageCrop/cropModalUtils';
import styles from '../profileMediaModal.module.scss';

const TITLE_ID = 'ProfilePictureModal-title';
const DESC_ID = 'ProfilePictureModal-desc';

function resolveExistingAvatarSrc(avatars) {
  if (!avatars || avatars.placeholder) {
    return null;
  }
  if (avatars.temp) {
    return avatars.temp;
  }
  const images = avatars.images || {};
  const size =
    images['380'] || images['280'] || images['190'] || images['140'] || null;
  if (size && size.square && size.square.main) {
    return size.square.main;
  }
  return null;
}

/**
 * Profile picture edit modal — circular crop, live 96px / 32px previews.
 * Shares CropWorkspace with Adjust thumbnail (maskShape=circle).
 */
const ProfilePictureModal = ({
  show,
  avatars,
  displayName,
  onCancel,
  onSave,
  onRemove,
}) => {
  const dialogRef = useRef(null);
  const cropperRef = useRef(null);
  const fileInputRef = useRef(null);
  const previousFocusRef = useRef(null);
  const previewTimerRef = useRef(null);

  const [src, setSrc] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewError, setPreviewError] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(0.1);
  const [maxZoom, setMaxZoom] = useState(3);
  const [ready, setReady] = useState(false);
  const [removed, setRemoved] = useState(false);

  const existingSrc = resolveExistingAvatarSrc(avatars);
  const hasPhoto = Boolean(src) && !removed;
  const canRemove = hasPhoto || Boolean(existingSrc);

  const getCropper = useCallback(() => {
    return cropperRef.current && cropperRef.current.cropper
      ? cropperRef.current.cropper
      : null;
  }, []);

  const updatePreview = useCallback(() => {
    const cropper = getCropper();
    if (!cropper) {
      return;
    }

    try {
      const canvas = cropper.getCroppedCanvas({
        width: 192,
        height: 192,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'low',
      });
      if (canvas) {
        setPreviewUrl(canvas.toDataURL('image/jpeg', 0.75));
        setPreviewError(false);
      } else {
        setPreviewUrl(null);
        setPreviewError(true);
      }
    } catch (err) {
      // Cross-origin taint — don't fall back to the original remote URL.
      setPreviewUrl(null);
      setPreviewError(true);
    }

    try {
      const imageData = cropper.getImageData();
      if (imageData && imageData.naturalWidth) {
        setZoom(imageData.width / imageData.naturalWidth);
      }
    } catch (err) {
      // ignore
    }
  }, [getCropper]);

  const schedulePreview = useCallback(() => {
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
    }
    previewTimerRef.current = setTimeout(() => {
      previewTimerRef.current = null;
      updatePreview();
    }, PREVIEW_THROTTLE_MS);
  }, [updatePreview]);

  const syncZoomBounds = useCallback(() => {
    const cropper = getCropper();
    if (!cropper) {
      return;
    }

    try {
      const imageData = cropper.getImageData();
      const canvasData = cropper.getCanvasData();
      if (!imageData || !imageData.naturalWidth || !canvasData) {
        return;
      }

      const coverZoom = Math.max(
        canvasData.width / imageData.naturalWidth,
        canvasData.height / imageData.naturalHeight
      );
      const nextMin = Number.isFinite(coverZoom) ? coverZoom * 0.98 : 0.1;
      const nextMax = Math.max(nextMin * 4, 3);

      setMinZoom(nextMin);
      setMaxZoom(nextMax);
      setZoom(imageData.width / imageData.naturalWidth);
    } catch (err) {
      // ignore
    }
  }, [getCropper]);

  const loadSource = useCallback(async (file, url) => {
    setReady(false);
    setPreviewUrl(null);
    setPreviewError(false);
    setRemoved(false);

    if (file instanceof File || file instanceof Blob) {
      const dataUrl = await readFileAsDataUrl(file);
      setSrc(dataUrl);
      setOriginalImage(file);
      return;
    }

    if (url) {
      setSrc(url);
      setOriginalImage(url);
      return;
    }

    setSrc(null);
    setOriginalImage(null);
  }, []);

  useEffect(() => {
    if (!show) {
      return undefined;
    }

    setRemoved(false);
    if (existingSrc) {
      loadSource(null, existingSrc);
    } else {
      setSrc(null);
      setOriginalImage(null);
      setPreviewUrl(null);
      setPreviewError(false);
      setReady(false);
    }

    return () => {
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
      }
    };
  }, [show, existingSrc, loadSource]);

  useEffect(() => {
    if (!show) {
      return undefined;
    }

    return attachCropDialogChrome({
      dialogRef,
      getCropper,
      src,
      onNudge: schedulePreview,
      previousFocusRef,
    });
  }, [show, getCropper, schedulePreview, src]);

  const handleReady = () => {
    const cropper = getCropper();
    if (!cropper) {
      return;
    }

    setReady(true);
    cropper.reset();
    syncZoomBounds();
    updatePreview();
  };

  const handleCropperZoom = (event) => {
    const cropper = getCropper();
    const ratio =
      event && event.detail && Number.isFinite(event.detail.ratio)
        ? event.detail.ratio
        : null;

    if (cropper && ratio != null) {
      if (ratio > maxZoom) {
        event.preventDefault();
        cropper.zoomTo(maxZoom);
        setZoom(maxZoom);
        schedulePreview();
        return;
      }
      if (ratio < minZoom) {
        event.preventDefault();
        cropper.zoomTo(minZoom);
        setZoom(minZoom);
        schedulePreview();
        return;
      }
      setZoom(ratio);
    }

    schedulePreview();
  };

  const handleResetCrop = () => {
    const cropper = getCropper();
    if (!cropper) {
      return;
    }
    cropper.reset();
    syncZoomBounds();
    updatePreview();
  };

  const handleZoomSlider = (event) => {
    const cropper = getCropper();
    if (!cropper) {
      return;
    }

    const next = Number(event.target.value);
    if (!Number.isFinite(next)) {
      return;
    }

    cropper.zoomTo(next);
    setZoom(next);
    schedulePreview();
  };

  const handleFileChange = async (event) => {
    const input = event.target;
    const file = input && input.files && input.files[0];
    if (!file) {
      return;
    }
    await loadSource(file, null);
    // Allow re-selecting the same file later. Capture `input` before await —
    // React 16 pools synthetic events so event.target is null afterwards.
    if (input) {
      input.value = '';
    } else if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openPicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemove = () => {
    if (
      typeof window !== 'undefined' &&
      !window.confirm(
        'Remove your profile picture? You’ll go back to the default avatar.'
      )
    ) {
      return;
    }

    setRemoved(true);
    setSrc(null);
    setOriginalImage(null);
    setPreviewUrl(null);
    setPreviewError(false);
    setReady(false);

    if (onRemove) {
      onRemove();
    }
  };

  const handleSave = () => {
    if (!hasPhoto) {
      openPicker();
      return;
    }

    const cropper = getCropper();
    if (!cropper || !src) {
      return;
    }

    const data = cropper.getData(true);
    // API requires crop.avatar.w === crop.avatar.h; getData(true) can
    // round width/height independently and differ by 1px.
    const side = Math.max(3, Math.round(Math.min(data.width, data.height)));
    let croppedImage = null;
    try {
      const canvas = cropper.getCroppedCanvas({
        width: 380,
        height: 380,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });
      croppedImage = canvas ? canvas.toDataURL('image/jpeg', 0.9) : null;
    } catch (err) {
      croppedImage = null;
    }

    onSave({
      x: data.x < 0 ? 0 : data.x,
      y: data.y < 0 ? 0 : data.y,
      width: side,
      height: side,
      image: originalImage,
      croppedImage,
    });
  };

  const defaultAvatar = (
    <div className={styles.defaultAvatar} aria-hidden="true">
      <User size={40} strokeWidth={1.75} />
    </div>
  );

  const defaultAvatarSm = (
    <div className={styles.defaultAvatarSm} aria-hidden="true">
      <User size={16} strokeWidth={1.75} />
    </div>
  );

  // Live crop export only — never fall back to the original remote URL
  // (uncropped and misleading when the canvas is tainted).
  const displayPreview = hasPhoto && !previewError ? previewUrl : null;

  const preview96 = previewError ? (
    <div className={styles.previewUnavailable} role="status">
      Preview unavailable
    </div>
  ) : displayPreview ? (
    <img src={displayPreview} alt="" />
  ) : (
    defaultAvatar
  );

  const preview32 = previewError ? (
    <div
      className={`${styles.previewUnavailable} ${styles.previewUnavailableSm}`}
      role="status"
      aria-label="Preview unavailable"
    />
  ) : displayPreview ? (
    <img src={displayPreview} alt="" />
  ) : (
    defaultAvatarSm
  );

  const removeButton = canRemove ? (
    <button type="button" className={styles.removeLink} onClick={handleRemove}>
      Remove photo
    </button>
  ) : null;

  return (
    <Modal
      show={show}
      onExit={onCancel}
      backgroundExit
      closeButton={false}
      animation="scale"
      modalClassName={styles.modal}
      modalStyle={{
        width: 'calc(100% - 24px)',
        maxWidth: 880,
        boxSizing: 'border-box',
      }}
    >
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        aria-describedby={DESC_ID}
        tabIndex={-1}
      >
        <header className={styles.header}>
          <h2 className={styles.title} id={TITLE_ID}>
            Profile picture
          </h2>
          <p className={styles.subhead} id={DESC_ID}>
            Shown on your profile, in client searches and next to your messages.
          </p>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onCancel}
            aria-label="Cancel"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </header>

        <div className={styles.body}>
          <div className={styles.workspaceColumn}>
            <CropWorkspace
              src={hasPhoto ? src : null}
              cropperRef={cropperRef}
              aspectRatio={1}
              maskShape="circle"
              cropBoxResizable={false}
              onReady={handleReady}
              onCrop={schedulePreview}
              onZoom={handleCropperZoom}
              zoom={zoom}
              minZoom={minZoom}
              maxZoom={maxZoom}
              onZoomChange={handleZoomSlider}
              onReset={handleResetCrop}
              showZoom={hasPhoto}
              emptyTitle="Drop a photo here or browse"
              emptyHint="JPG or PNG · at least 400×400px"
              emptyIcon={<User size={48} strokeWidth={1.5} />}
              onEmptyClick={openPicker}
              fileInputRef={fileInputRef}
              onFileChange={handleFileChange}
              hint="Drag to reposition · pinch or scroll to zoom"
            />
          </div>

          {hasPhoto ? (
            <div className={styles.previewColumn}>
              <p className={styles.previewEyebrow}>Preview</p>
              <div className={styles.profileCircle}>{preview96}</div>
              <p className={styles.profileCircleLabel}>Your profile</p>

              <div className={styles.searchPreview}>
                <div className={styles.searchAvatar}>{preview32}</div>
                <div className={styles.searchMeta}>
                  <p className={styles.searchTitle}>
                    {displayName || 'Your name'}
                  </p>
                  <p className={styles.searchCaption}>
                    Search results and messages
                  </p>
                </div>
              </div>

              <button
                type="button"
                className={styles.textLink}
                onClick={openPicker}
              >
                Upload a different photo
              </button>
            </div>
          ) : null}

          {removeButton ? (
            <div className={styles.removeInBody}>{removeButton}</div>
          ) : null}
        </div>

        <footer className={styles.footer}>
          <div className={`${styles.footerLeft} ${styles.removeInFooter}`}>
            {removeButton}
          </div>
          <div className={styles.footerRight}>
            <button
              type="button"
              className={styles.saveButton}
              onClick={handleSave}
              disabled={hasPhoto && !ready}
            >
              {hasPhoto ? 'Save photo' : 'Upload photo'}
            </button>
          </div>
        </footer>
      </div>
    </Modal>
  );
};

ProfilePictureModal.propTypes = {
  show: PropTypes.bool.isRequired,
  avatars: PropTypes.object,
  displayName: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
};

ProfilePictureModal.defaultProps = {
  avatars: null,
  displayName: '',
  onRemove: null,
};

export default ProfilePictureModal;
